import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Search, CheckCircle2, XCircle, Handshake, PackageCheck } from "lucide-react";
import { api, money, formatApiError } from "@/lib/api";

const LOGO_ICON = "/logo-emblem.png";

const STATUS_LABELS = {
  pending: "Under review — we'll reply within 24 hours",
  appraised: "Valuation complete",
  countered: "We've made you a counter offer",
  trade_offered: "We've offered you store credit",
  customer_countered: "You countered — we're reviewing it",
  accepted: "Agreed — deal on",
  posted: "Parcel on its way to us",
  declined: "Closed",
  completed: "Verified — payment released",
};

const EVENT_TEXT = (h) => {
  const amt = h.amount != null ? ` ${money(h.amount)}` : "";
  const bonus = h.bonus_pct ? ` (+${h.bonus_pct}% bonus)` : "";
  const map = {
    submitted: "submitted the offer",
    appraise: `sent a valuation of${amt}`,
    counter: `countered at${amt}`,
    trade: `offered store credit of${amt}${bonus}`,
    accept: "accepted the offer",
    posted: "marked the parcel as posted",
    payout_submitted: "sent their bank details",
    paid: "confirmed payment sent",
    verify: "verified the item — payment released",
    decline: "declined",
    approve: "approved the return",
    decline_return: "declined the return",
  };
  return map[h.action] || h.action;
};

const inputCls =
  "w-full bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3.5 text-sm text-[#0A0A0A] placeholder-[#a79f90] focus:outline-none focus:border-[#B38728] transition-colors";

export default function Track() {
  const [params] = useSearchParams();
  const [ticket, setTicket] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(null);
  const [counterAmount, setCounterAmount] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [payout, setPayout] = useState({ account_name: "", sort_code: "", account_number: "" });
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [acting, setActing] = useState("");
  const [message, setMessage] = useState("");

  const doLookup = async (t, e) => {
    setLoading(true);
    setView(null);
    setMessage("");
    try {
      const { data } = await api.get("/track", { params: { ticket: t, email: e } });
      setView(data);
    } catch (err) {
      setMessage(formatApiError(err, "No request found with that reference and email"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = params.get("ticket");
    const e = params.get("email");
    if (t && e) {
      setTicket(t);
      setEmail(e);
      doLookup(t, e);
      window.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookup = async (e) => {
    e.preventDefault();
    doLookup(ticket, email);
  };

  const respond = async (action) => {
    setActing(action);
    setMessage("");
    try {
      const { data } = await api.post("/track/respond", {
        ticket, email, action,
        amount: counterAmount ? parseFloat(counterAmount) : undefined,
      });
      setView(data);
      setCounterAmount("");
    } catch (err) {
      setMessage(formatApiError(err, "Could not send your response"));
    } finally {
      setActing("");
    }
  };

  const markPosted = async () => {
    setActing("posted");
    setMessage("");
    try {
      const { data } = await api.post("/track/posted", {
        ticket, email, tracking: trackingNumber || undefined,
      });
      setView(data);
      setTrackingNumber("");
    } catch (err) {
      setMessage(formatApiError(err, "Could not mark as posted"));
    } finally {
      setActing("");
    }
  };

  const awaitingCustomer = view && view.kind === "offer" && ["countered", "trade_offered"].includes(view.status);
  const needsPayout = view && view.kind === "offer" && view.deal_kind !== "credit"
    && ["accepted", "posted", "completed"].includes(view.status) && !view.paid;

  const submitPayout = async () => {
    setActing("payout");
    setMessage("");
    try {
      const { data } = await api.post("/track/payout", { ticket, email, ...payout });
      setView(data);
      setShowPayoutForm(false);
      setPayout({ account_name: "", sort_code: "", account_number: "" });
    } catch (err) {
      setMessage(formatApiError(err, "Could not save your bank details"));
    } finally {
      setActing("");
    }
  };
  const currentOffer = view?.current_offer;
  const offerTotal = currentOffer?.amount != null
    ? currentOffer.bonus_pct
      ? currentOffer.amount * (1 + currentOffer.bonus_pct / 100)
      : currentOffer.amount
    : null;

  return (
    <main className="min-h-screen bg-[#F3EDE3] text-[#0A0A0A] font-sans antialiased" data-testid="track-page">
      <header className="border-b border-[#DCD3C3] bg-[#F3EDE3]/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="track-logo">
            <img src={LOGO_ICON} alt="Stud Exchange" className="w-10 h-10 rounded-full object-cover" />
            <div className="leading-none">
              <p className="font-serif font-semibold tracking-wide text-sm">STUD EXCHANGE</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-[#B38728] mt-1">BUY • SELL • TRADE • COLLECT</p>
            </div>
          </Link>
          <Link to="/" data-testid="track-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#5C564C] hover:text-[#B38728] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Storefront
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#B38728] flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-[#B38728]" /> Offer Tracker
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-5">Track your offer</h1>
        <p className="text-[#5C564C] text-sm mt-4 max-w-xl">
          Every offer and valuation carries a reference number (it's in your emails, like SX-0001). Pop it in below with the email you used, and you can see exactly where things stand — and respond — without a single email chain.
        </p>

        <form onSubmit={lookup} className="mt-8 bg-white border border-[#DCD3C3] p-6 sm:p-8" data-testid="track-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Reference Number</label>
              <input required value={ticket} onChange={(e) => setTicket(e.target.value)} data-testid="track-ticket-input" className={`mt-2 ${inputCls}`} placeholder="SX-0001" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="track-email-input" className={`mt-2 ${inputCls}`} placeholder="The email you submitted with" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="track-lookup-button"
            className="mt-6 bg-[#0A0A0A] text-[#F3EDE3] px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50 flex items-center gap-3"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? "Finding…" : "Find My Offer"}
          </button>
          {message && <p className="mt-4 text-sm text-red-700" data-testid="track-error">{message}</p>}
        </form>

        {view && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 bg-white border border-[#DCD3C3] p-6 sm:p-8"
            data-testid="track-status-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728]" data-testid="track-ticket-label">{view.ticket}</p>
                <h2 className="font-serif text-2xl mt-2">{view.item_title || (view.kind === "valuation" ? "Valuation request" : "Offer")}</h2>
              </div>
              <span className="bg-[#121110] text-[#D4AF37] font-mono text-[9px] uppercase tracking-[0.25em] px-3 py-1.5" data-testid="track-status-chip">
                {STATUS_LABELS[view.status] || view.status}
              </span>
            </div>

            <div className="mt-5 text-sm text-[#5C564C] space-y-1">
              {view.asking_price && <p><span className="text-[#B38728] font-mono text-xs uppercase">Your asking price:</span> {/^\d/.test(String(view.asking_price)) ? `£${view.asking_price}` : view.asking_price}</p>}
              {offerTotal != null && (
                <p className="text-base" data-testid="track-current-offer">
                  <span className="text-[#B38728] font-mono text-xs uppercase">Our current offer:</span>{" "}
                  <strong className="text-[#0A0A0A]">{money(offerTotal)}{currentOffer.bonus_pct ? ` store credit (${money(currentOffer.amount)} + ${currentOffer.bonus_pct}% bonus)` : ""}</strong>
                  {currentOffer.final && (
                    <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#B38728] border border-[#B38728] px-1.5 py-0.5 align-middle" data-testid="track-final-badge">Final offer</span>
                  )}
                </p>
              )}
              {view.appraised_value != null && (
                <p className="text-base" data-testid="track-appraised">
                  <span className="text-[#B38728] font-mono text-xs uppercase">Appraised value:</span>{" "}
                  <strong className="text-[#0A0A0A]">{money(view.appraised_value)}</strong>
                </p>
              )}
            </div>

            {awaitingCustomer && (
              <div className="mt-6 border border-[#D4AF37]/50 bg-[#D4AF37]/5 p-5" data-testid="track-actions">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-4">Your move — how would you like to respond?</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => respond("accept")}
                    disabled={!!acting}
                    data-testid="track-accept-button"
                    className="flex items-center gap-2 bg-[#0A0A0A] text-[#F3EDE3] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-green-800 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-4 h-4" /> {acting === "accept" ? "Sending…" : "Accept Offer"}
                  </button>
                  <button
                    onClick={() => respond("decline")}
                    disabled={!!acting}
                    data-testid="track-decline-button"
                    className="flex items-center gap-2 border border-[#DCD3C3] text-[#5C564C] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-red-700 hover:text-red-700 transition-colors disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" /> {acting === "decline" ? "Sending…" : "Decline"}
                  </button>
                </div>
                {currentOffer?.final ? (
                  <p className="mt-4 text-sm text-[#5C564C]" data-testid="track-final-note">
                    This is our final offer — it's as far as the vault can stretch on this one. Accept or decline above.
                  </p>
                ) : (
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <input
                      type="number" min="1" step="0.01"
                      value={counterAmount}
                      onChange={(e) => setCounterAmount(e.target.value)}
                      placeholder="Your new asking price (£)"
                      data-testid="track-counter-amount-input"
                      className={`${inputCls} w-56`}
                    />
                    <button
                      onClick={() => respond("counter")}
                      disabled={!counterAmount || !!acting}
                      data-testid="track-counter-button"
                      className="flex items-center gap-2 border border-[#B38728] text-[#B38728] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                    >
                      <Handshake className="w-4 h-4" /> {acting === "counter" ? "Sending…" : "Send Counter"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {view.status === "accepted" && view.kind === "offer" && (
              <div className="mt-6 border border-[#D4AF37]/50 bg-[#D4AF37]/5 p-5" data-testid="track-posted-panel">
                <p className="text-sm text-[#5C564C]" data-testid="track-accepted-note">
                  Deal agreed — your postage &amp; packing instructions are in your inbox (a separate email). Post the item to the address in that email with your reference
                  written on a slip inside the parcel, then come back here and let us know it's on its way.
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mt-4 mb-3">Posted it? Tell us here</p>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Tracking number (optional)"
                    data-testid="track-posted-tracking-input"
                    className={`${inputCls} w-64`}
                  />
                  <button
                    onClick={markPosted}
                    disabled={!!acting}
                    data-testid="track-posted-button"
                    className="flex items-center gap-2 bg-[#0A0A0A] text-[#F3EDE3] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                  >
                    <PackageCheck className="w-4 h-4" /> {acting === "posted" ? "Sending…" : "I've Posted It"}
                  </button>
                </div>
              </div>
            )}

            {needsPayout && (
              <div className="mt-6 border border-[#D4AF37]/50 bg-[#D4AF37]/5 p-5" data-testid="track-payout-panel">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-3">Where should we send your money?</p>
                {view.payout_submitted && !showPayoutForm ? (
                  <div data-testid="track-payout-saved">
                    <p className="text-sm text-[#5C564C]">
                      Bank details received — as soon as your item is verified, payment goes straight to your account by bank transfer (Faster Payments — usually minutes, always within 2 hours of sending).
                    </p>
                    <button
                      onClick={() => setShowPayoutForm(true)}
                      data-testid="track-payout-update-button"
                      className="mt-3 border border-[#DCD3C3] text-[#5C564C] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#B38728] hover:text-[#B38728] transition-colors"
                    >
                      Update bank details
                    </button>
                  </div>
                ) : (
                  <div data-testid="track-payout-form">
                    <p className="text-sm text-[#5C564C] mb-4">
                      Pop in the bank account you'd like payment sent to. We only ever need these three things — <strong>never</strong> your card details or passwords.
                    </p>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <input
                        value={payout.account_name}
                        onChange={(e) => setPayout({ ...payout, account_name: e.target.value })}
                        placeholder="Account holder name"
                        data-testid="track-payout-name-input"
                        className={inputCls}
                      />
                      <input
                        value={payout.sort_code}
                        onChange={(e) => setPayout({ ...payout, sort_code: e.target.value })}
                        placeholder="Sort code (00-00-00)"
                        data-testid="track-payout-sortcode-input"
                        className={inputCls}
                      />
                      <input
                        value={payout.account_number}
                        onChange={(e) => setPayout({ ...payout, account_number: e.target.value })}
                        placeholder="Account number (8 digits)"
                        data-testid="track-payout-account-input"
                        className={inputCls}
                      />
                    </div>
                    <button
                      onClick={submitPayout}
                      disabled={!!acting || !payout.account_name || !payout.sort_code || !payout.account_number}
                      data-testid="track-payout-submit-button"
                      className="mt-4 bg-[#0A0A0A] text-[#F3EDE3] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                    >
                      {acting === "payout" ? "Saving…" : "Save Payout Details"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {view.credit_code && (
              <div className="mt-6 border border-[#B38728] bg-[#FDF9EF] p-5" data-testid="track-credit-panel">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-2">Your store credit</p>
                <p className="font-mono text-2xl tracking-[0.3em]" data-testid="track-credit-code">{view.credit_code}</p>
                <p className="text-sm text-[#5C564C] mt-2" data-testid="track-credit-remaining">
                  Balance: <strong className="text-[#0A0A0A]">{view.credit_remaining != null ? money(view.credit_remaining) : "—"}</strong>
                  {view.credit_amount != null && view.credit_remaining !== view.credit_amount && <> of {money(view.credit_amount)}</>}
                  {" — "}enter the code in your basket at checkout. Spend some now, the rest later — it never expires.
                </p>
              </div>
            )}

            {view.paid && (
              <p className="mt-6 text-sm text-[#5C564C] border border-green-800/30 bg-green-50 p-4" data-testid="track-paid-note">
                Payment sent by bank transfer — it usually lands within minutes. A pleasure doing business with you.
              </p>
            )}

            {view.status === "posted" && (
              <p className="mt-6 text-sm text-[#5C564C] border border-[#B38728]/40 bg-[#D4AF37]/5 p-4" data-testid="track-posted-note">
                Your parcel is on its way to the vault{view.tracking_number ? <> — tracking <strong className="text-[#0A0A0A]">{view.tracking_number}</strong></> : ""}.
                The moment it arrives and passes verification, payment is released within 2 working days.
              </p>
            )}

            {view.status === "completed" && view.kind === "offer" && !view.paid && !view.credit_code && (
              <p className="mt-6 text-sm text-[#5C564C] border border-green-800/30 bg-green-50 p-4" data-testid="track-completed-note">
                Verified and complete — payment is on its way to your bank{view.payout_submitted ? "" : " — make sure your payout details above are saved"}.
              </p>
            )}

            {view.history?.length > 0 && (
              <div className="mt-6 border-t border-[#DCD3C3] pt-4" data-testid="track-history">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] mb-3">Negotiation trail</p>
                <div className="space-y-2">
                  {view.history.map((h, i) => (
                    <p key={i} className="text-xs font-mono text-[#5C564C]">
                      <span className={h.actor === "stud" ? "text-[#B38728]" : "text-[#0A0A0A]"}>
                        {h.actor === "stud" ? "STUD EXCHANGE" : "YOU"}
                      </span>{" "}
                      {EVENT_TEXT(h)}{" "}
                      <span className="opacity-50">· {new Date(h.at).toLocaleDateString()}</span>
                    </p>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </main>
  );
}
